<template>
  <div class="flex flex-col gap-y-4 text-left" data-testid="send-record-step-3">
    <div>
      <p class="text-sm font-bold text-textPrimary mb-1">Tipo do registro *</p>
      <!--
        Balão ⑬: catálogo GLOBAL. O que a escala não aceita aparece DESABILITADO com o motivo —
        nunca some da lista, para não parecer erro de carregamento.
      -->
      <!--
        Um único grupo de rádio (mesmo `name`): a exclusividade passa a viver no
        próprio HTML, e não só no `activityType`. O `disabled` mora no INPUT — é
        ele que o teclado pula e que os testes conferem; `<label>` não aceita o
        atributo.
      -->
      <div class="flex flex-col gap-1">
        <label
          v-for="option in typeOptions"
          :key="option.value"
          class="flex items-center gap-2 rounded border px-3 py-2 text-left text-sm"
          :class="
            activityType === option.value
              ? 'border-primary bg-primary5/30 text-primary30 font-bold'
              : option.disabled
                ? 'border-gray-200 text-neutralLight cursor-not-allowed'
                : 'border-gray-200 text-textPrimary cursor-pointer'
          "
          :data-testid="`send-record-type-${option.value}`"
        >
          <input
            type="radio"
            :name="ACTIVITY_TYPE_RADIO_GROUP"
            :class="option.disabled ? 'cursor-not-allowed' : 'accent-primary'"
            :value="option.value"
            :checked="activityType === option.value"
            :disabled="option.disabled || undefined"
            :data-testid="`send-record-type-${option.value}-input`"
            @change="emit('update:activityType', option.value)"
          />
          <span class="flex-1">
            {{ option.label }}
            <span v-if="option.value === ActivityType.OTHER" class="text-xs text-neutral50">
              — exige descrição
            </span>
          </span>
          <span v-if="option.disabled" class="text-xs">{{ option.reason }}</span>
        </label>
      </div>
    </div>

    <!--
      Em "Ajuste administrativo" a justificativa mora no passo seguinte (a variação compacta),
      junto da equivalência — é lá que ela faz sentido para quem audita.
    -->
    <div v-if="showDescription">
      <label class="text-sm font-bold text-textPrimary" for="send-record-description">
        Descrição {{ isDescriptionRequired ? '*' : '(opcional)' }}
      </label>
      <!--
        `border-solid` não é redundante ao lado de `border`: o reset do Vuetify
        (`textarea { border-style: none }`, importado depois do `tailwind.css`)
        anula o `solid` que o preflight dá a `*`, e sem ele a classe `border`
        define só a largura — o campo ficava sem borda nenhuma.
      -->
      <textarea
        id="send-record-description"
        data-testid="send-record-description"
        rows="3"
        maxlength="2000"
        class="w-full rounded border border-solid border-gray-200 px-2 py-1 text-sm"
        :value="description"
        @input="emit('update:description', ($event.target as HTMLTextAreaElement).value)"
      ></textarea>
      <p class="text-xs text-neutral50">
        Obrigatória quando o tipo é "Outros" (mínimo {{ minLength }} caracteres).
        {{ description.trim().length }}/2000
      </p>
    </div>

    <div>
      <p class="text-sm font-bold text-textPrimary mb-1">
        Anexos {{ attachmentRequired ? '*' : '(opcional)' }}
      </p>
      <!--
        Dropzone no molde do `AttendanceUploadModal/StepFile.vue`: mesmos formatos, mesmo limite
        de 50 MB e — principalmente — a MESMA materialização com `arrayBuffer()`. Não é reuso do
        componente porque aquele é de arquivo único e aqui são 1..10 (Q14).

        I6 — o anexo é obrigatório em quase todos os tipos, então o passo não pode depender do
        mouse. O `<input type="file">` fica `sr-only` (alcançável pelo tab e acionável por
        Enter/Espaço, de graça pelo navegador) com um `<label for>` visível; arrastar e soltar
        continua no `<div>`, que agora é só a área de drop.
      -->
      <div
        data-testid="send-record-dropzone"
        class="border-2 border-dashed border-primary30 rounded-2xl bg-primary5/30 p-6 flex flex-col items-center justify-center cursor-pointer"
        @click="handleZoneClick"
        @dragover.prevent
        @drop.prevent="handleDrop"
      >
        <input
          id="send-record-file-input"
          ref="fileInput"
          type="file"
          class="sr-only"
          multiple
          accept=".jpeg,.jpg,.png,.pdf"
          aria-describedby="send-record-dropzone-hint"
          data-testid="send-record-file-input"
          @change="handleChange"
        />
        <p class="text-sm text-textPrimary">Arraste os anexos aqui ou</p>
        <label
          for="send-record-file-input"
          class="mt-2 rounded-md border-2 border-primary30 px-4 py-1 text-sm font-bold text-primary30 cursor-pointer"
          data-testid="send-record-file-select"
        >
          Selecionar arquivos
        </label>
        <p id="send-record-dropzone-hint" class="text-xs text-primary30 mt-1">
          JPG, PNG ou PDF · até 50 MB por arquivo · 1 a {{ maxFiles }} arquivos
        </p>
      </div>

      <ul
        v-if="fileErrors.length"
        class="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
        role="alert"
        data-testid="send-record-file-errors"
      >
        <li v-for="error in fileErrors" :key="error">{{ error }}</li>
      </ul>

      <div class="mt-2 flex flex-col gap-1">
        <div
          v-for="(file, index) in files"
          :key="`${file.name}-${index}`"
          class="flex items-center justify-between rounded border border-gray-200 px-3 py-2"
          data-testid="send-record-file-item"
        >
          <span class="flex flex-col">
            <span class="text-sm text-textPrimary truncate max-w-[240px]">{{ file.name }}</span>
            <span class="text-xs text-neutral50">{{ formatFileSize(file.size) }}</span>
          </span>
          <span class="flex items-center gap-2">
            <button
              type="button"
              class="text-xs text-primary30 underline"
              :data-testid="`send-record-file-preview-${index}`"
              @click="emit('update:activeIndex', index)"
            >
              Mostrar no painel
            </button>
            <button
              type="button"
              class="text-xs text-red-700 underline"
              :data-testid="`send-record-file-remove-${index}`"
              @click="removeFile(index)"
            >
              Remover
            </button>
          </span>
        </div>
      </div>

      <p v-if="files.length && stationCount > 1" class="text-xs text-neutral50 mt-1">
        Os {{ files.length }} anexos valem para os {{ stationCount }} plantões selecionados.
      </p>
    </div>

    <!--
      O anexo fica À VISTA antes do checklist: quem responde "está legível?" e
      "é do dia certo?" precisa estar OLHANDO para o arquivo, não lembrando dele.
      Abre no último anexado e pagina pelos demais; "Mostrar no painel" na lista só
      move o índice. O índice mora no MODAL, então o anexo que estava na tela aqui é
      o mesmo que abre nas etapas 4 e 5.
    -->
    <AttachmentPanel
      v-if="files.length"
      :files="files"
      :layout="isDesktop ? 'side' : 'carousel'"
      :active-index="activeIndex"
      @update:active-index="emit('update:activeIndex', $event)"
    />

    <!--
      Balão ⑯: checklist leve, no estilo do checklist da folha. Declaração, não validação —
      nenhum item é obrigatório. Ele NÃO viaja no envio: `selfCheck` não existe no
      `CreateActivityRecordDto` de §2.2 (I5). Enquanto o backend não aceitar o campo, a
      conferência é do emissor, aqui, antes do clique.
    -->
    <div>
      <p class="text-sm font-bold text-textPrimary mb-1">Antes de enviar, confira os anexos</p>
      <div class="flex flex-col gap-1 rounded-lg border border-gray-200 px-3 py-2">
        <label
          v-for="item in selfCheckItems"
          :key="item.key"
          class="flex items-start gap-2 text-sm text-textPrimary cursor-pointer"
          :data-testid="`send-record-self-check-${item.key}`"
        >
          <input
            type="checkbox"
            class="mt-0.5"
            :checked="selfCheck[item.key]"
            :data-testid="`send-record-self-check-checkbox-${item.key}`"
            @change="toggleSelfCheck(item.key)"
          />
          <span>{{ item.label }}</span>
        </label>
      </div>
      <p class="text-xs text-neutral50 mt-1">
        Nenhum item é obrigatório. A conferência é sua, aqui: ela não é enviada junto com o registro
        — quem audita vê os anexos.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import {
  ActivityType,
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_ATTACHMENT_LIMITS,
  ACTIVITY_DESCRIPTION_MIN_LENGTH
} from '@/constants/ds-attendance.constants'
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import AttachmentPanel from './AttachmentPanel.vue'
import {
  buildActivitySelfCheckItems,
  type ActivitySelfCheckKey,
  type ActivitySelfCheckState
} from './activity-self-check'
import {
  formatFileSize,
  requiresAttachment,
  requiresDescriptionOnTypeStep,
  validateAttachments
} from './types'

/**
 * Passo 3 de M2 — tipo do catálogo, descrição, anexos e checklist.
 *
 * "Ajuste administrativo" NÃO entra na lista do escalista (balão ⑭): ele é
 * exclusivo do perfil de backoffice/auditoria e não depende do catálogo da
 * escala — quem decide é a permissão, não a política.
 */

const props = defineProps<{
  activityType: ActivityType | null
  description: string
  files: File[]
  fileErrors: string[]
  selfCheck: ActivitySelfCheckState
  /** Catálogo aceito pela política. Vazio = política desconhecida ⇒ tudo aceito. */
  acceptedActivityTypes: ActivityType[]
  /**
   * Perfil de backoffice/auditoria COM política que aceita o Ajuste
   * administrativo (§2.6). O catálogo ainda é reconferido aqui.
   */
  showAdminAdjustment: boolean
  formattedDate: string
  stationCount: number
  /** Anexo em exibição — o modal é o dono, para as etapas 4 e 5 abrirem no mesmo. */
  activeIndex?: number | null
}>()

const emit = defineEmits<{
  'update:activityType': [value: ActivityType]
  'update:description': [value: string]
  'update:selfCheck': [value: ActivitySelfCheckState]
  'update:fileErrors': [errors: string[]]
  'add-files': [files: File[]]
  'remove-file': [index: number]
  'update:activeIndex': [value: number]
}>()

/** `name` compartilhado pelas opções: é ele que torna a escolha única. */
const ACTIVITY_TYPE_RADIO_GROUP = 'send-record-activity-type'

const toast = useToast()
const fileInput = ref<HTMLInputElement | null>(null)

/** O painel do anexo é o mesmo das etapas 4 e 5: coluna no desktop, faixa no celular. */
const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

const maxFiles = ACTIVITY_ATTACHMENT_LIMITS.maxFiles
const minLength = ACTIVITY_DESCRIPTION_MIN_LENGTH

/**
 * Remover um arquivo ANTES do que está na tela desloca todos os índices seguintes:
 * sem este ajuste o painel passaria a mostrar o vizinho. O índice novo vai ANTES da
 * remoção para o modal aplicar os dois na mesma rodada.
 */
function removeFile(index: number): void {
  const current = props.activeIndex ?? 0
  if (index < current) emit('update:activeIndex', current - 1)
  emit('remove-file', index)
}

const typeOptions = computed(() => {
  const accepted = props.acceptedActivityTypes
  const knowsPolicy = accepted.length > 0

  const catalog = Object.values(ActivityType).filter(
    (type) => type !== ActivityType.ADMIN_ADJUSTMENT
  )

  const options = catalog.map((type) => ({
    value: type,
    label: ACTIVITY_TYPE_LABELS[type],
    disabled: knowsPolicy && !accepted.includes(type),
    reason: 'Não aceito nesta escala'
  }))

  // §2.6 — o Ajuste administrativo exige perfil de auditoria E política que o
  // aceite; as duas condições já vêm resolvidas em `showAdminAdjustment`.
  if (props.showAdminAdjustment && accepted.includes(ActivityType.ADMIN_ADJUSTMENT)) {
    options.push({
      value: ActivityType.ADMIN_ADJUSTMENT,
      label: ACTIVITY_TYPE_LABELS[ActivityType.ADMIN_ADJUSTMENT],
      // C7 — a variação compacta é de um plantão só.
      disabled: props.stationCount > 1,
      reason: 'Disponível para um plantão por vez'
    })
  }

  return options
})

const showDescription = computed(() => props.activityType !== ActivityType.ADMIN_ADJUSTMENT)
const isDescriptionRequired = computed(() => requiresDescriptionOnTypeStep(props.activityType))
const attachmentRequired = computed(() => requiresAttachment(props.activityType))

const selfCheckItems = computed(() =>
  buildActivitySelfCheckItems({ formattedDate: props.formattedDate })
)

function toggleSelfCheck(key: ActivitySelfCheckKey): void {
  emit('update:selfCheck', { ...props.selfCheck, [key]: !props.selfCheck[key] })
}

function triggerFileInput(): void {
  fileInput.value?.click()
}

/**
 * Clicar em qualquer ponto da área ainda abre o seletor — menos quando o clique
 * já foi no `<label>`/`<input>`, que o abrem sozinhos: reabrir daqui mostraria
 * o diálogo duas vezes.
 */
function handleZoneClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  if (target?.closest('label, input')) return
  triggerFileInput()
}

function handleChange(event: Event): void {
  const target = event.target as HTMLInputElement
  void processIncoming(Array.from(target.files ?? []))
}

function handleDrop(event: DragEvent): void {
  void processIncoming(Array.from(event.dataTransfer?.files ?? []))
}

/**
 * Valida ANTES de materializar (a ordem do `StepFile.vue`: um arquivo de 62 MB
 * não precisa ir para a memória para ser recusado) e materializa só o que
 * passou. O que já estava anexado nunca é perdido — erro A de M2.
 *
 * A materialização com `arrayBuffer()` é obrigatória: em navegadores mobile o
 * `File` original é uma referência "lazy" (CamScanner/Files) que pode ficar
 * inacessível na hora do upload. NÃO remover — regressão conhecida (#1430
 * adicionou, #1751 removeu por engano, e voltou).
 */
async function processIncoming(incoming: File[]): Promise<void> {
  if (!incoming.length) return

  const { accepted, errors } = validateAttachments(incoming, props.files)
  emit('update:fileErrors', errors)

  const materialized: File[] = []
  for (const file of accepted) {
    try {
      const buffer = await file.arrayBuffer()
      materialized.push(
        new File([buffer], file.name, { type: file.type, lastModified: file.lastModified })
      )
    } catch (error) {
      console.error('[send-record] falha ao materializar anexo:', error)
      // eslint-disable-next-line no-restricted-syntax -- falha local ao ler o anexo, não erro de API
      toast.error(`Não foi possível carregar "${file.name}". Tente novamente.`)
    }
  }

  if (fileInput.value) fileInput.value.value = ''
  if (materialized.length) emit('add-files', materialized)
}
</script>
